/**
 * Explosions, skill waves, lightning, pickups and telegraphs. Effects are a
 * pure function of (kind, age, id); bursts and boss deaths are remembered by
 * the renderer so they can outlive the engine's short effect list.
 */
import type { Effect, FlightState } from "../types";
import { weaponInfo } from "../content";
import { FONT, INK, TAU, box, disc, glow, hash, line, oval, poly, radial, rgba, stamp } from "./kit";
import { icon } from "./icons";
import { DANGER } from "./enemies";

type Ctx = CanvasRenderingContext2D;
const LIFE = { burst: 44, boss: 150, ink: 54 } as const;
type Long = { x: number; y: number; kind: keyof typeof LIFE; start: number; id: number };
const remembered = new Map<number, Long>();
let run = "";
let lastTick = -1;

function fireball(c: Ctx, x: number, y: number, r: number, stage: number, alpha: number) {
  if (r <= 0 || alpha <= 0) return;
  const s = Math.max(0, Math.min(3, Math.floor(stage)));
  c.globalAlpha = alpha;
  stamp(c, "fireball:" + s, 64, 64, (g) => {
    const tones = [
      ["#FFFFFF", "#FFF3B0", "#FFB23E"],
      ["#FFF6C8", "#FFB23E", "#F2542D"],
      ["#FFC46B", "#F2542D", "#8E2B2B"],
      ["#8A6A60", "#5A4646", "#3A3036"],
    ][s];
    g.fillStyle = radial(g, 0, 0, 0, 32, [
      [0, tones[0]],
      [0.45, tones[1]],
      [0.8, rgba(tones[2], 0.85)],
      [1, rgba(tones[2], 0)],
    ]);
    g.fillRect(-32, -32, 64, 64);
  }, x, y, 0, r / 26);
  c.globalAlpha = 1;
}
function smoke(c: Ctx, x: number, y: number, r: number, alpha: number, dark = false) {
  if (alpha <= 0) return;
  c.globalAlpha = alpha;
  stamp(c, "smoke:" + (dark ? 1 : 0), 64, 64, (g) => {
    const tone = dark ? "#3A3A44" : "#D8DCE4";
    g.fillStyle = radial(g, 0, 0, 0, 32, [
      [0, rgba(tone, 0.9)],
      [0.6, rgba(tone, 0.5)],
      [1, rgba(tone, 0)],
    ]);
    g.fillRect(-32, -32, 64, 64);
  }, x, y, 0, r / 28);
  c.globalAlpha = 1;
}

/** Mid-air kill: flash, fireball cluster, shrapnel, sparks and smoke. */
function explosion(c: Ctx, x: number, y: number, t: number, id: number, scale = 1) {
  const s = scale;
  if (t < 6) {
    glow(c, x, y, (46 - t * 5) * s, "#FFF3C4", 1);
    disc(c, x, y, (18 - t * 2.5) * s, `rgba(255,255,255,${1 - t / 6})`);
  }
  if (t < 16) {
    c.strokeStyle = `rgba(255,250,235,${0.85 * (1 - t / 16)})`;
    c.lineWidth = 5 * (1 - t / 16) * s + 0.5;
    c.beginPath();
    c.arc(x, y, (10 + t * 5.2) * s, 0, TAU);
    c.stroke();
  }
  for (let i = 0; i < 6; i++) {
    const a = hash(id, i) * TAU,
      d = (6 + hash(id, i, 2) * 14) * s * Math.min(1, t / 8),
      life = t / (22 + hash(id, i, 3) * 8);
    if (life < 1) fireball(c, x + Math.cos(a) * d, y + Math.sin(a) * d + t * 0.4, (12 + hash(id, i, 4) * 12) * s * (0.6 + life * 0.8), life * 3.2, 1 - life * life);
  }
  for (let i = 0; i < 5; i++) {
    const start = 6 + i * 2,
      life = (t - start) / 34;
    if (life <= 0 || life >= 1) continue;
    const a = hash(id, i, 9) * TAU;
    smoke(c, x + Math.cos(a) * 16 * s, y + Math.sin(a) * 12 * s + life * 26, (12 + life * 20) * s, 0.45 * (1 - life), i % 2 === 0);
  }
  for (let i = 0; i < 9; i++) {
    const a = hash(id, i, 11) * TAU,
      v = (3 + hash(id, i, 12) * 5) * s,
      k = (1 - Math.pow(0.9, t)) / 0.1,
      px = x + Math.cos(a) * v * k,
      py = y + Math.sin(a) * v * k + t * 0.35;
    if (t > 34) break;
    c.save();
    c.translate(px, py);
    c.rotate(t * 0.3 * (i % 2 ? 1 : -1) + a);
    c.globalAlpha = 1 - t / 34;
    poly(c, [[-4 * s, -2 * s], [4 * s, -3 * s], [2 * s, 3 * s]], i % 3 ? "#3B3F4E" : "#E9DEC8", INK, 1);
    c.restore();
  }
  if (t < 14)
    for (let i = 0; i < 8; i++) {
      const a = hash(id, i, 21) * TAU,
        d = (8 + t * (5 + hash(id, i, 22) * 5)) * s;
      line(c, [[x + Math.cos(a) * d, y + Math.sin(a) * d], [x + Math.cos(a) * (d + 9 * s), y + Math.sin(a) * (d + 9 * s)]], `rgba(255,236,160,${1 - t / 14})`, 2.4 * s);
    }
}
function bossDeath(c: Ctx, x: number, y: number, t: number, id: number) {
  for (let n = 0; n < 12; n++) {
    const start = n * 7,
      local = t - start;
    if (local < 0 || local > 44) continue;
    explosion(c, x + (hash(id, n, 1) - 0.5) * 200, y + (hash(id, n, 2) - 0.5) * 150, local, id * 31 + n, 0.9 + hash(id, n, 3) * 0.6);
  }
  const fin = t - 90;
  if (fin >= 0 && fin < 60) {
    explosion(c, x, y, fin, id * 97, 3);
    c.strokeStyle = `rgba(255,236,190,${1 - fin / 60})`;
    c.lineWidth = 14 * (1 - fin / 60);
    c.beginPath();
    c.arc(x, y, 40 + fin * 18, 0, TAU);
    c.stroke();
  }
}
/** 墨爆: an ink wave that sweeps the whole screen, spattering colour. */
function inkWave(c: Ctx, x: number, y: number, t: number, id: number) {
  const k = t / LIFE.ink,
    radius = 30 + (1 - Math.pow(1 - k, 2)) * 1200;
  c.save();
  c.globalAlpha = Math.max(0, 1 - k);
  c.strokeStyle = "rgba(30,40,80,0.55)";
  c.lineWidth = 60 * (1 - k) + 6;
  c.beginPath();
  c.arc(x, y, radius, 0, TAU);
  c.stroke();
  c.strokeStyle = "rgba(120,220,255,0.8)";
  c.lineWidth = 6;
  c.beginPath();
  c.arc(x, y, radius + 26 * (1 - k), 0, TAU);
  c.stroke();
  for (let i = 0; i < 28; i++) {
    const a = hash(id, i) * TAU,
      d = radius * (0.7 + hash(id, i, 2) * 0.35),
      r = (8 + hash(id, i, 3) * 22) * (1 - k * 0.6);
    const colors = ["#23305C", "#3E9BDC", "#E4572E", "#F2C230", "#23305C", "#7A5CC8"];
    disc(c, x + Math.cos(a) * d, y + Math.sin(a) * d, r, colors[i % colors.length]);
  }
  c.restore();
  if (t < 10) glow(c, x, y, 200 * (1 - t / 10), "#9FE7FF", 1);
}
function lightning(c: Ctx, e: Effect) {
  const tx = e.toX ?? e.x,
    ty = e.toY ?? e.y,
    life = 1 - e.age / 12,
    seed = e.id * 13 + Math.floor(e.age / 2),
    dx = tx - e.x,
    dy = ty - e.y,
    len = Math.hypot(dx, dy) || 1,
    nx = -dy / len,
    ny = dx / len,
    pts: number[][] = [[e.x, e.y]];
  for (let i = 1; i < 8; i++) {
    const off = (hash(seed, i) - 0.5) * Math.min(38, len * 0.25);
    pts.push([e.x + (dx * i) / 8 + nx * off, e.y + (dy * i) / 8 + ny * off]);
  }
  pts.push([tx, ty]);
  c.save();
  c.globalAlpha = Math.max(0, life);
  line(c, pts, "rgba(120,200,255,0.45)", 12);
  line(c, pts, "#8FD8FF", 5);
  line(c, pts, "#FFFFFF", 2);
  const fork = pts[3 + (seed % 3)];
  line(c, [fork, [fork[0] + nx * 24 + dx * 0.08, fork[1] + ny * 24 + dy * 0.08]], "#CFF1FF", 2);
  c.restore();
  glow(c, tx, ty, 26 * life, "#8FD8FF", 1);
}
function spark(c: Ctx, e: Effect) {
  const t = e.age;
  if (t > 8) return;
  const k = t / 8;
  glow(c, e.x, e.y, 22 * (1 - k), "#FFF1B8", 1);
  for (let i = 0; i < 4; i++) {
    const a = hash(e.id, i) * TAU,
      d = 6 + t * 3;
    line(c, [[e.x + Math.cos(a) * d, e.y + Math.sin(a) * d], [e.x + Math.cos(a) * (d + 7), e.y + Math.sin(a) * (d + 7)]], `rgba(255,248,210,${1 - k})`, 2.2);
  }
}
function halo(c: Ctx, e: Effect, color: string, rays: boolean) {
  const t = e.age,
    k = t / 100;
  if (rays && t < 60) {
    c.save();
    c.translate(e.x, e.y);
    c.rotate(t * 0.03);
    c.globalAlpha = 0.55 * (1 - t / 60);
    for (let i = 0; i < 10; i++) {
      c.rotate(TAU / 10);
      poly(c, [[-7, 30], [7, 30], [0, 150 + (i % 2) * 40]], rgba(color, 0.8), null);
    }
    c.restore();
  }
  if (t < 40) {
    c.strokeStyle = rgba(color, 1 - t / 40);
    c.lineWidth = 6 * (1 - t / 40) + 1;
    c.beginPath();
    c.arc(e.x, e.y, 30 + t * 4, 0, TAU);
    c.stroke();
  }
  for (let i = 0; i < 8; i++) {
    const a = hash(e.id, i) * TAU,
      d = 30 + hash(e.id, i, 2) * 50,
      rise = t * (0.6 + hash(e.id, i, 3) * 0.8);
    if (k > 0.8) break;
    c.save();
    c.translate(e.x + Math.cos(a) * d, e.y + Math.sin(a) * d * 0.6 - rise);
    c.globalAlpha = 1 - k / 0.8;
    icon(c, "star", 0, 0, 10 + (i % 3) * 3, i % 2 ? color : "#FFFFFF", rgba(INK, 0.6));
    c.restore();
  }
}
function label(c: Ctx, e: Effect, color: string) {
  if (!e.label) return;
  const t = e.age,
    pop = t < 8 ? 0.6 + (t / 8) * 0.55 : t < 14 ? 1.15 - ((t - 8) / 6) * 0.15 : 1,
    y = e.y - 70 - Math.min(40, t * 0.6);
  c.save();
  c.globalAlpha = t > 75 ? Math.max(0, 1 - (t - 75) / 25) : 1;
  c.translate(e.x, y);
  c.scale(pop, pop);
  c.font = `900 26px ${FONT}`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  const w = c.measureText(e.label).width + 34;
  box(c, -w / 2, -21, w, 42, 21, "rgba(27,33,48,0.82)", color, 3);
  c.lineJoin = "round";
  c.strokeStyle = INK;
  c.lineWidth = 5;
  c.strokeText(e.label, 0, 1);
  c.fillStyle = "#FFF6DA";
  c.fillText(e.label, 0, 1);
  c.restore();
}

/**
 * Paints all effects in world coordinates. Returns the screen-flash strength
 * (0..1) from boss deaths for the HUD to apply.
 */
export function paintEffects(c: Ctx, s: FlightState): number {
  if (s.id !== run || s.tick < lastTick) {
    remembered.clear();
    run = s.id;
  }
  lastTick = s.tick;
  for (const e of s.effects)
    if ((e.kind === "burst" || e.kind === "boss" || e.kind === "ink") && !remembered.has(e.id))
      remembered.set(e.id, { x: e.x, y: e.y, kind: e.kind, start: s.tick - e.age, id: e.id });
  let flash = 0;
  for (const [id, fx] of remembered) {
    const t = s.tick - fx.start;
    if (t >= LIFE[fx.kind]) {
      remembered.delete(id);
      continue;
    }
    if (fx.kind === "burst") explosion(c, fx.x, fx.y, t, id, 1.4);
    else if (fx.kind === "boss") {
      bossDeath(c, fx.x, fx.y, t, id);
      if (t >= 90 && t < 110) flash = Math.max(flash, 1 - (t - 90) / 20);
    } else inkWave(c, fx.x, fx.y, t, id);
  }
  if (remembered.size > 90) remembered.delete(remembered.keys().next().value!);
  for (const e of s.effects) {
    if (e.kind === "hit") spark(c, e);
    else if (e.kind === "chain") lightning(c, e);
    else if (e.kind === "rescue") {
      for (let i = 0; i < 2; i++) {
        const t = (e.age + i * 12) % 25;
        c.strokeStyle = `rgba(90,230,160,${1 - t / 25})`;
        c.lineWidth = 4;
        c.beginPath();
        c.arc(e.x, e.y, 20 + t * 3, 0, TAU);
        c.stroke();
      }
    } else if (e.kind === "hurt") {
      if (e.age < 14) {
        glow(c, e.x, e.y, 70 * (1 - e.age / 14), DANGER, 1);
        c.strokeStyle = `rgba(255,80,100,${1 - e.age / 14})`;
        c.lineWidth = 4;
        c.beginPath();
        c.arc(e.x, e.y, 24 + e.age * 4, 0, TAU);
        c.stroke();
      }
    } else if (e.kind === "evolve") {
      halo(c, e, "#FFC94D", true);
      label(c, e, "#FFC94D");
    } else if (e.kind === "supply") {
      halo(c, e, "#6BE3A8", false);
      label(c, e, "#6BE3A8");
    }
  }
  return flash;
}

/* ------------------------------------------------------------ pickups */

const PICKUP: Record<string, [string, string, string]> = {
  repair: ["#4FD08A", "plus", "修理"],
  shield: ["#4FB7FF", "shield", "护盾"],
  power: ["#FF8A3D", "power", "火力"],
  weapon: ["#FFC94D", "", "换装"],
};
export function paintPickups(c: Ctx, s: FlightState) {
  for (const item of s.pickups ?? []) {
    const [color, glyph] = PICKUP[item.kind] ?? PICKUP.power,
      bob = Math.sin(s.tick * 0.08 + item.id) * 4,
      blink = item.life < 180 && Math.floor(s.tick / 6) % 2 === 0;
    if (blink) continue;
    c.save();
    c.translate(item.x, item.y + bob);
    c.save();
    c.rotate(s.tick * 0.02);
    c.globalAlpha = 0.35;
    for (let i = 0; i < 6; i++) {
      c.rotate(TAU / 6);
      poly(c, [[-5, 20], [5, 20], [0, 42]], color, null);
    }
    c.restore();
    glow(c, 0, 0, 34, color, 0.6);
    if (item.kind === "weapon") {
      const weapon = item.weapon ? weaponInfo(item.weapon) : null;
      box(c, -19, -19, 38, 38, 8, weapon?.color ?? color, INK, 3);
      box(c, -19, -19, 38, 10, 5, "rgba(255,255,255,0.35)", null);
      icon(c, item.weapon ?? "star", 0, 1, 24, "#FFFFFF");
    } else {
      stamp(c, "pickup:" + item.kind, 50, 50, (g) => {
        g.beginPath();
        for (let i = 0; i < 6; i++) g.lineTo(Math.cos((i / 6) * TAU + Math.PI / 6) * 20, Math.sin((i / 6) * TAU + Math.PI / 6) * 20);
        g.closePath();
        g.fillStyle = radial(g, -6, -8, 0, 26, [
          [0, "#FFFFFF"],
          [0.35, color],
          [1, color],
        ]);
        g.fill();
        g.strokeStyle = INK;
        g.lineWidth = 3;
        g.stroke();
        icon(g, glyph, 0, 1, 22, "#FFFFFF");
      });
    }
    c.restore();
  }
}

/* ---------------------------------------------------------- telegraphs */

export function paintWarnings(c: Ctx, s: FlightState) {
  for (const e of s.enemies) {
    if (!e.boss && e.kind === 8 && e.age < 110) {
      const k = e.age / 110;
      c.save();
      c.globalAlpha = 0.35 + k * 0.5;
      for (let i = 0; i < 4; i++) {
        const y = e.y + e.radius + 20 + ((i * 40 + e.age * 2) % 160);
        poly(c, [[e.x - 14, y], [e.x, y + 12], [e.x + 14, y], [e.x + 14, y + 7], [e.x, y + 19], [e.x - 14, y + 7]], DANGER, null);
      }
      c.restore();
    }
    if (e.warnAt && e.warnAt > s.tick && [3, 9, 12].includes(e.kind)) {
      const a = e.aim ?? Math.PI / 2,
        pulse = 0.5 + 0.5 * Math.sin(s.tick * 0.5);
      c.save();
      c.strokeStyle = rgba(DANGER, 0.3 + pulse * 0.35);
      c.lineWidth = 2;
      c.setLineDash([10, 8]);
      c.lineDashOffset = -s.tick * 1.5;
      c.beginPath();
      c.moveTo(e.x, e.y);
      c.lineTo(e.x + Math.cos(a) * 1400, e.y + Math.sin(a) * 1400);
      c.stroke();
      c.setLineDash([]);
      c.restore();
      c.strokeStyle = rgba(DANGER, 0.8);
      c.lineWidth = 2;
      c.beginPath();
      c.arc(e.x, e.y, e.radius + 6 + pulse * 5, 0, TAU);
      c.stroke();
    }
  }
}

/** Event ribbon in logical screen space (after the camera is undone). */
export function paintBanner(c: Ctx, s: FlightState) {
  if (!s.banner || s.banner.until <= s.tick) return;
  const left = s.banner.until - s.tick,
    enter = Math.min(1, (180 - Math.min(180, left)) / 12 + 0.001),
    alpha = Math.min(1, left / 30);
  c.save();
  c.globalAlpha = alpha;
  c.translate(360, 270);
  c.scale(0.8 + enter * 0.2, 0.8 + enter * 0.2);
  c.font = `900 30px ${FONT}`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  const w = Math.min(640, c.measureText(s.banner.text).width + 90);
  poly(c, [[-w / 2 - 22, -30], [w / 2 + 22, -30], [w / 2 + 6, 0], [w / 2 + 22, 30], [-w / 2 - 22, 30], [-w / 2 - 6, 0]], "rgba(27,33,48,0.86)", "#FFC94D", 3);
  oval(c, -w / 2 + 14, 0, 6, 6, "#FFC94D");
  oval(c, w / 2 - 14, 0, 6, 6, "#FFC94D");
  c.lineJoin = "round";
  c.strokeStyle = INK;
  c.lineWidth = 6;
  c.strokeText(s.banner.text, 0, 2);
  c.fillStyle = "#FFF3D1";
  c.fillText(s.banner.text, 0, 2);
  c.restore();
}
